import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { Routes } from "~/routes";
import { Footer } from "~/components/Footer";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { ManagerListPage } from "~/modules/managers/pages/list/component";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { queryManagers } from "~/modules/managers/api/router";
import { DEFAULT_MANAGER_LIST_QUERY } from "~/modules/managers/consts";
import {
    AggregateFunctions,
    AggregateQueryMode,
    ApiQuery,
    Filter,
    FilterOperators,
    QueryResponse,
    SortDirections,
} from "~/modules/filters/types";
import {
    getRwaAssetCountFromManager,
    getRwaHoldersFromManager,
    ManagerWithAggregates,
} from "~/modules/managers";
import {
    NetFlowsData,
    queryNetFlows,
} from "~/modules/visualization/util/queryNetFlows";
import {
    ASSET_CLASS_IDS,
    MEASURE_IDS,
    MEASURE_SLUGS,
    SOCIAL_OG_IMAGE_URL,
    TOKENIZATION_TYPE_IDS,
} from "~/consts";
import {
    AggregateItem,
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { Props as AggregateDisplayProps } from "~/components/AggregateDisplay";
import { getAssetManagerAggregates } from "~/modules/managers/lib/getAssetManagerAggregates";
import {
    LeagueTableGroupTypes,
    LeagueTableTabsByDistributionType,
} from "~/modules/league_tables/types";
import { queryTokenAggregates } from "~/modules/token_timeseries/api/router";
import { THIRTY_DAYS_AGO } from "~/modules/visualization/components/TimeSeriesChartV3/consts";
import { calculatePercentChange } from "~/utils/calculatePercentChange";
import { queryTokenAggregateValueWith30D } from "~/modules/token_timeseries/api/queryTokenAggregateValue";
import { queryLeagueTablesByDistributionType } from "~/modules/league_tables/api/queryLeagueTablesByDistributionType";

// // // //

const SEO_TITLE = "Asset Managers";
const SEO_DESCRIPTION =
    "Analyze operational performance and the competitive landscape of asset managers overseeing tokenized asset portfolios.";

// // // //

function getValuesFromAggregates(props: {
    aggregates: AggregateItem[];
    aggregates30D: AggregateItem[];
    managerID: number;
}): {
    val: number;
    val_30d: number;
    chg_30d_pct: number;
} {
    const { aggregates, aggregates30D, managerID } = props;

    const value: AggregateItem | undefined = aggregates.find(
        (aggregate) => aggregate.group.id === managerID,
    );

    const value30D: AggregateItem | undefined = aggregates30D.find(
        (aggregate) => aggregate.group.id === managerID,
    );

    const change30D = calculatePercentChange({
        initialValue: value30D ? value30D.value : 0,
        finalValue: value ? value.value : 0,
    });

    return {
        val: value ? value.value : 0,
        val_30d: value30D ? value30D.value : 0,
        chg_30d_pct: change30D,
    };
}

function getTokenAggregatesQuery(props: {
    tokenizationTypeID: number;
    thirtyDaysAgo?: boolean;
    groupBy: TokenTimeseriesGroupBy;
}): ApiQuery {
    const { tokenizationTypeID, thirtyDaysAgo = false, groupBy } = props;

    const filters: Filter[] = [
        {
            field: TokenTimeseriesColumns.measureSlug,
            operator: FilterOperators.Equals,
            value: MEASURE_SLUGS.bridged_token_value_dollar,
        },
        {
            field: TokenTimeseriesColumns.tokenizationTypeID,
            operator: FilterOperators.Equals,
            value: tokenizationTypeID,
        },
        // NOTE - currently excluding stablecoins from the asset-managers page
        {
            field: TokenTimeseriesColumns.assetClassID,
            operator: FilterOperators.NotEquals,
            value: ASSET_CLASS_IDS.Stablecoin,
        },
        // Excludes tokens where managerID isn't present (must be greater than 0)
        {
            field: TokenTimeseriesColumns.managerID,
            operator: FilterOperators.GreaterThan,
            value: 0,
        },
    ];

    if (thirtyDaysAgo) {
        filters.push({
            operator: FilterOperators.Equals,
            field: TokenTimeseriesColumns.date,
            value: THIRTY_DAYS_AGO.toISOString(),
        });
    }

    const query: ApiQuery = {
        filter: {
            operator: FilterOperators.And,
            filters,
        },
        aggregate: {
            groupBy,
            aggregateFunction: AggregateFunctions.Sum,
            mode: AggregateQueryMode.stock,
        },
        sort: {
            direction: SortDirections.asc,
            field: TokenTimeseriesColumns.date,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    };

    return query;
}

export async function getStaticProps() {
    const [
        listQueryResponse,
        netFlows,
        leagueTableTabs,

        // Page-level Aggregates
        distributedAggregates,
        representedAggregates,

        // Manager-level Aggregates
        distributedTokenAggregates,
        distributedTokenAggregates30D,
        representedTokenAggregates,
        representedTokenAggregates30D,
    ] = await Promise.all([
        queryManagers({
            query: {
                ...DEFAULT_MANAGER_LIST_QUERY,
                pagination: {
                    page: 1,
                    perPage: 250,
                },
            },
        }),

        queryNetFlows({
            name: "asset-managers-net-flows",
            groupBy: TokenTimeseriesGroupBy.manager,
            filters: [
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
                {
                    field: TokenTimeseriesColumns.managerID,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.NotEquals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
            ],
        }),

        queryLeagueTablesByDistributionType({
            name: "asset-managers",
            groups: [LeagueTableGroupTypes.managers],
            filter: {
                field: TokenTimeseriesColumns.managerID,
                operator: FilterOperators.IsNotNull,
                value: null,
            },
        }),

        // Page-level Aggregates - Distributed
        queryTokenAggregateValueWith30D({
            name: "asset-managers--distributed-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
                {
                    field: TokenTimeseriesColumns.managerID,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
            ],
        }),

        // Page-level Aggregates - Represented
        queryTokenAggregateValueWith30D({
            name: "asset-managers--represented-asset-value",
            measureID: MEASURE_IDS.BridgedTokenValueDollar,
            filters: [
                {
                    field: TokenTimeseriesColumns.tokenizationTypeID,
                    operator: FilterOperators.Equals,
                    value: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                },
                {
                    field: TokenTimeseriesColumns.assetClassID,
                    operator: FilterOperators.NotEquals,
                    value: ASSET_CLASS_IDS.Stablecoin,
                },
                {
                    field: TokenTimeseriesColumns.managerID,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
            ],
        }),

        // Manager-level Aggregates - Distributed
        queryTokenAggregates({
            query: getTokenAggregatesQuery({
                tokenizationTypeID: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                groupBy: TokenTimeseriesGroupBy.manager,
            }),
        }),

        // Manager-level Aggregates - Distributed 30D
        queryTokenAggregates({
            query: getTokenAggregatesQuery({
                thirtyDaysAgo: true,
                tokenizationTypeID: TOKENIZATION_TYPE_IDS.OnChainDistributed,
                groupBy: TokenTimeseriesGroupBy.manager,
            }),
        }),

        // Manager-level Aggregates - Represented
        queryTokenAggregates({
            query: getTokenAggregatesQuery({
                tokenizationTypeID: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                groupBy: TokenTimeseriesGroupBy.manager,
            }),
        }),

        // Manager-level Aggregates - Represented 30D
        queryTokenAggregates({
            query: getTokenAggregatesQuery({
                thirtyDaysAgo: true,
                tokenizationTypeID: TOKENIZATION_TYPE_IDS.OnChainRepresented,
                groupBy: TokenTimeseriesGroupBy.manager,
            }),
        }),
    ]);

    // // // //

    // Calculate page-level aggregates
    const totalDistributedValues = {
        val: distributedAggregates.value,
        val_30d: distributedAggregates.value30D,
        chg_30d_pct: distributedAggregates.change30D,
    };

    const totalRepresentedValues = {
        val: representedAggregates.value,
        val_30d: representedAggregates.value30D,
        chg_30d_pct: representedAggregates.change30D,
    };

    const aggregates = getAssetManagerAggregates({
        managers: listQueryResponse.results,
        totalDistributedValues,
        totalRepresentedValues,
    });

    // // // //

    // Iterate over each manager and add the distributed and represented token aggregates to the manager
    // ENHANCEMENT - move this into DBT so it's automatically available in the API
    const managersWithAggregates: QueryResponse<ManagerWithAggregates> = {
        ...listQueryResponse,
        results: listQueryResponse.results
            .map((manager) => {
                const distributed = getValuesFromAggregates({
                    aggregates: distributedTokenAggregates.results,
                    aggregates30D: distributedTokenAggregates30D.results,
                    managerID: manager.id,
                });

                const represented = getValuesFromAggregates({
                    aggregates: representedTokenAggregates.results,
                    aggregates30D: representedTokenAggregates30D.results,
                    managerID: manager.id,
                });

                const managerWithAggregates: ManagerWithAggregates = {
                    ...manager,
                    rwa_asset_count: getRwaAssetCountFromManager(manager),
                    rwa_holders_count: getRwaHoldersFromManager(manager),
                    distributed_value: {
                        ...distributed,
                    },
                    represented_value: {
                        ...represented,
                    },
                };

                return managerWithAggregates;
            })
            // Exclude managers without any RWA assets
            .filter((manager) => manager.rwa_asset_count > 0),
    };

    return {
        props: {
            aggregates,
            listQueryResponse: managersWithAggregates,
            netFlows,
            leagueTableTabs,
        },
        // Next.js will re-generate this page based on NEXTJS_REVALIDATE_TIMEOUT const
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function ManagersPage(props: {
    aggregates: Array<AggregateDisplayProps>;
    listQueryResponse: QueryResponse<ManagerWithAggregates>;
    netFlows: NetFlowsData;
    leagueTableTabs: LeagueTableTabsByDistributionType;
}) {
    const { aggregates, listQueryResponse, netFlows, leagueTableTabs } = props;
    return (
        <PageTrack>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.managers)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.managers),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "asset managers, RWA managers, tokenized fund managers, institutional managers, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.managers}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.managers,
                    },
                ]}
            />
            <UserFetcher>{({ user }) => <Navbar user={user} />}</UserFetcher>
            <SidebarLayout pathname={Routes.managers}>
                <Breadcrumbs
                    pages={[
                        {
                            name: SEO_TITLE,
                            current: false,
                        },
                    ]}
                />
                <PageWrapper>
                    <ManagerListPage
                        aggregates={aggregates}
                        listQueryResponse={listQueryResponse}
                        netFlows={netFlows}
                        leagueTableTabs={leagueTableTabs}
                    />
                    <Footer />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
